import { Card, LinkWrapper, Pictogram } from '@reverse-hr/pattern-library';
import { Link, useHref } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStoreActions, useStoreState } from 'easy-peasy';
import {
  downloadDataSelector,
  getCandidateCommitmentLetterSelector,
  getCandidateResignationLetterSelector,
  setDownloadDataSelector,
} from '@model/Candidate/Candidate.selectors';
import { useDownloadFile } from '@utils/hooks/use-download-file';
import {
  CANDIDATES_SEGMENT,
  SUCCESS_FEE_SEGMENT,
} from '@app/constants/navigation.constants';
import { memo } from 'react';
import { selectorIsCompensationVisible } from '@model/showcase-table';
import classNames from 'classnames';
import { useUserPackageVisibility } from '@components/ShareLinkModal/_hooks/use-user-package-visibility';
import {
  Action,
  ACTION_VARIANTS,
  Icon,
  ICON_NAMES,
} from '@reverse-hr/design-system';

export const ClosingHiredCard = memo(({ closing, jobId }) => {
  const { t } = useTranslation();
  const candidateHref = `/jobs/${jobId}/candidates/${closing.id}`;
  const successFeeHref = `/jobs/${jobId}/${CANDIDATES_SEGMENT}/${closing.id}/${SUCCESS_FEE_SEGMENT}`;

  const downloadData = useStoreState(downloadDataSelector);
  const setDownloadData = useStoreActions(setDownloadDataSelector);
  const isCompensationVisible = useStoreState(selectorIsCompensationVisible);
  const userPackageVisibility = useUserPackageVisibility();

  useDownloadFile({
    url: downloadData?.url,
    filename: downloadData?.filename,
    cb: () => setDownloadData(null),
  });

  const getResignationLetter = useStoreActions(
    getCandidateResignationLetterSelector,
  );

  const getCommitmentLetter = useStoreActions(
    getCandidateCommitmentLetterSelector,
  );

  const handleDownload = async handleDownloadThunk => {
    return await handleDownloadThunk({
      candidateId: closing.id,
      jobId,
    });
  };

  const cardModifier = classNames('u-card-fixed c-hiring-card', {
    'c-hiring-card--padded':
      !isCompensationVisible || !userPackageVisibility.isVisible,
  });

  return (
    <div>
      <Card
        order={['head', 'media', 'body', 'actions']}
        modifier={cardModifier}
      >
        {{
          head: {
            left: (
              <div className="c-card__titles">
                <div className="titles-texts">
                  <p className="c-card__title">
                    {t('common.formatting.fullName', {
                      firstName: closing.firstName,
                      lastName: closing.lastName,
                    })}
                  </p>
                </div>
              </div>
            ),
            right: (
              <Link to={candidateHref}>
                <Icon name={ICON_NAMES.ID_CARD} />
              </Link>
            ),
          },
          body: isCompensationVisible && userPackageVisibility.isVisible && (
            <>
              {!!closing.offer.hasCommitmentLetter && (
                <LetterLink
                  onClick={() => handleDownload(getCommitmentLetter)}
                  label={t('closing.links.commitment_letter')}
                />
              )}

              {!!closing.offer.hasResignationLetter && (
                <LetterLink
                  onClick={() => handleDownload(getResignationLetter)}
                  label={t('closing.links.resignation_letter')}
                />
              )}
            </>
          ),
          actions: isCompensationVisible && (
            <>
              {!!closing.offer.successFeeRecap && (
                <Link
                  className="c-hiring-card__success-fee-link"
                  to={successFeeHref}
                >
                  <Action
                    rightIcon={ICON_NAMES.CHEVRON_BIG_RIGHT}
                    variant={ACTION_VARIANTS.OUTLINE}
                  >
                    {t('closing.links.prospect')}
                  </Action>
                </Link>
              )}
            </>
          ),
        }}
      </Card>
    </div>
  );
});

const LetterLink = ({ url, label, onClick }) => (
  <div>
    <button
      type="unstyled"
      external
      onClick={onClick}
      target="_blank"
      className="u-card-fixed__letter-button"
    >
      <Pictogram
        modifier=""
        icon="icn-download"
        size="small"
        children={label}
      />
    </button>
  </div>
);
